import React, { FC } from 'react'
import classNames from 'classnames'
import { Text } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheaterPreferences } from '../TheaterPreferencesContext'
import { DAYS_OF_WEEK, TIME_WINDOWS } from '../constants'

interface DayTimePreferencesListProps {
  dayTimePreferences: Record<string, string[]>
}

const TimeWindowButton: FC<{
  isSelected: boolean
  label: string
  dayId: string
  windowId: string
}> = ({ isSelected, label, dayId, windowId }) => {
  const { handleDayTimeToggle } = useTheaterPreferences()

  return (
    <button
      onClick={() => handleDayTimeToggle(dayId, windowId)}
      className={classNames(
        'rounded-full px-4 py-1.5 text-xs font-normal transition-colors',
        isSelected ? 'bg-black text-white' : 'bg-gray-300 text-black border border-black',
      )}
    >
      {label}
    </button>
  )
}

export const DayTimePreferencesList: FC<DayTimePreferencesListProps> = ({ dayTimePreferences }) => {
  const { t } = useTranslate('account')

  // Get all days that have an entry in dayTimePreferences (selected days)
  const selectedDayIds = Object.keys(dayTimePreferences)

  if (selectedDayIds.length === 0) {
    return (
      <Text className="text-xs text-gray-500">
        {t('noDaysAndTimesSelected', 'No preferred days and times selected')}
      </Text>
    )
  }

  const sortedDayIds = [...selectedDayIds].sort((a, b) => {
    const aIndex = DAYS_OF_WEEK.findIndex((d) => d.id === a)
    const bIndex = DAYS_OF_WEEK.findIndex((d) => d.id === b)
    return aIndex - bIndex
  })

  return (
    <div className="flex flex-col gap-4">
      {sortedDayIds.map((dayId) => {
        const timeWindows = dayTimePreferences[dayId] || []
        const dayLabel = DAYS_OF_WEEK.find((d) => d.id === dayId)?.label || ''

        return (
          <div key={dayId} className="flex flex-col gap-2 rounded-xl bg-gray-300 p-4">
            <Text weight="semibold" className="text-sm">
              {dayLabel}
            </Text>
            <div className="flex flex-wrap gap-2">
              {TIME_WINDOWS.map((window) => (
                <TimeWindowButton
                  key={window.id}
                  isSelected={timeWindows.includes(window.id)}
                  label={window.label}
                  dayId={dayId}
                  windowId={window.id}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
